import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Image, Text, VStack, HStack, Link, Icon, Divider, Button } from "@chakra-ui/react";
import { AiOutlineInstagram, AiOutlineYoutube } from "react-icons/ai";
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";

const AboutPage = () => {
  const { t } = useTranslation('about');
  const navigate = useNavigate();
  const { lng } = useParams<{ lng: string }>();
  const currentLanguage = lng || 'en';

  const getLocalizedPath = (path: string) => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `/${currentLanguage}${cleanPath}`;
  };

  return (
    <Box
      maxWidth="800px"
      margin="0 auto"
      padding="20px"
      textAlign="center"
    >
      {/* Image Section */}
      <Box
        marginBottom="20px"
        display="inline-block"
      >
        <Image
        src="/images/about.jpg"
        alt={t('intro.imageAlt')}
        objectFit="contain"
        objectPosition="center"
        loading="lazy"
        />
      </Box>

      {/* About Text */}
      <VStack spacing="20px" align="center">
        <Text fontSize="lg" fontWeight="normal" lineHeight="1.8" textAlign="center">
          {t('intro.greeting')} {t('intro.specialization')}
        </Text>

        {/* Services Section */}
        <Box textAlign="center" mt={6}>
          <Text fontSize="xl" fontWeight="bold" mb={4}>
            {t('services.title')}
          </Text>
          <VStack spacing={3} align="stretch" maxW="600px">
            <Text fontSize="md" textAlign="left">
              <strong>{t('services.event.title')}</strong> {t('services.event.description')}
            </Text>
            <Text fontSize="md" textAlign="left">
              <strong>{t('services.portrait.title')}</strong> {t('services.portrait.description')}
            </Text>
            <Text fontSize="md" textAlign="left">
              <strong>{t('services.development.title')}</strong> {t('services.development.description')}
            </Text>
            <Text fontSize="md" textAlign="left">
              <strong>{t('services.artistic.title')}</strong> {t('services.artistic.description')}
            </Text>
          </VStack>
        </Box>

        <Text fontSize="md" fontWeight="normal" lineHeight="1.6" textAlign="center" fontStyle="italic" color="gray.600" maxW="500px">
          {t('serviceArea.text')}
        </Text>

        <Text fontSize="lg" fontWeight="normal" lineHeight="1.8" textAlign="center">
          {t('cta.booking')}
        </Text>

        <Divider my={8} />

        {/* Contact & Business Information */}
        <Box w="100%" maxW="600px">
          <Text fontSize="2xl" fontWeight="bold" mb={6} textAlign="center">
            {t('contact.title')}
          </Text>
          
          <VStack spacing={6} align="center" mb={6}>
            <Box textAlign="center">
              <HStack spacing={2} justifyContent="center" mb={1}>
                <Icon as={FaPhone} color="gray.600" boxSize="14px" />
                <Text fontWeight="medium" fontSize="sm" color="gray.600">{t('contact.phone')}</Text>
              </HStack>
              <Link href="tel:+34679581425" fontSize="lg" fontWeight="medium" color="gray.600">
                +34 679 581 425
              </Link>
            </Box>
            
            <Box textAlign="center">
              <HStack spacing={2} justifyContent="center" mb={1}>
                <Icon as={FaEnvelope} color="gray.600" boxSize="14px" />
                <Text fontWeight="medium" fontSize="sm" color="gray.600">{t('contact.email')}</Text>
              </HStack>
              <Link href="mailto:algranoconpablo@gmail.com" fontSize="lg" fontWeight="medium" color="gray.600">
                algranoconpablo@gmail.com
              </Link>
            </Box>

            <HStack spacing={4} align="center" w="100%" justifyContent="center">
              <Box w="20px" display="flex" justifyContent="center">
                <Icon as={FaMapMarkerAlt} color="gray.600" boxSize="16px" />
              </Box>
              <Box textAlign="center">
                <Text fontWeight="medium" fontSize="sm" color="gray.600">{t('contact.location')}</Text>
                <Text fontSize="lg" fontWeight="medium">{t('contact.locationCity')}</Text>
                <Text fontSize="sm" color="gray.500">{t('contact.locationCountry')}</Text>
              </Box>
            </HStack>

            <Box textAlign="center">
              <Text fontWeight="medium" fontSize="sm" color="gray.600" mb={2}>{t('contact.serviceAreas')}</Text>
              <VStack align="center" spacing={1}>
                <Text fontSize="sm">{t('contact.areas.primary')}</Text>
                <Text fontSize="sm">{t('contact.areas.secondary')}</Text>
                <Text fontSize="sm">{t('contact.areas.tertiary')}</Text>
              </VStack>
            </Box>
          </VStack>

          {/* CTA Buttons */}
          <VStack spacing={3} mb={6}>
            <Button 
              as={Link} 
              onClick={() => navigate(getLocalizedPath("/services"))}
              cursor="pointer"
              colorScheme="blackAlpha"
              size="lg"
              w={{ base: "100%", md: "auto" }}
              _hover={{ textDecoration: 'none' }}
            >
              {t('buttons.viewServices')}
            </Button>
            <Button 
              as={Link} 
              href="mailto:algranoconpablo@gmail.com?subject=Photography Inquiry"
              variant="outline"
              colorScheme="blackAlpha"
              size="md"
              w={{ base: "100%", md: "auto" }}
              _hover={{ textDecoration: 'none' }}
            >
              {t('buttons.bookSession')}
            </Button>
          </VStack>

          {/* Social Media Links */}
          <Box textAlign="center">
            <Text fontSize="lg" fontWeight="bold" mb={4}>
              {t('social.title')}
            </Text>
            <HStack spacing="20px" justify="center">
              <Link href="https://www.instagram.com/algranoconpablo" isExternal>
                <VStack spacing={1}>
                  <Icon as={AiOutlineInstagram} boxSize="30px" />
                  <Text fontSize="sm">{t('social.instagram')}</Text>
                </VStack>
              </Link>
              <Link href="https://www.youtube.com/@algranoconPablo" isExternal>
                <VStack spacing={1}>
                  <Icon as={AiOutlineYoutube} boxSize="30px" />
                  <Text fontSize="sm">{t('social.youtube')}</Text>
                </VStack>
              </Link>
            </HStack>
          </Box>
        </Box>

        <Divider my={8} />

        {/* Why Choose Film Photography */}
        <Box w="100%" maxW="600px" textAlign="center">
          <Text fontSize="xl" fontWeight="bold" mb={4}>
            {t('whyFilm.title')}
          </Text>
          <Text fontSize="md" lineHeight="1.6" color="gray.600">
            {t('whyFilm.description')}
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};

export default AboutPage;